import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Notiflix from "notiflix";
import {
  Modal,
  ScrollView,
  Text,
  Button,
  VStack,
  HStack,
  Box,
  Checkbox,
  Input,
  IconButton,
  CheckIcon,
  DeleteIcon,
  Center,
  InputLeftElement,
  AlertDialog,
} from "native-base";
import { v4 as uuidv4 } from "uuid";
import { MdModeEditOutline } from "react-icons/md";
import { getFirestore } from "redux-firestore";

// ==================== CONSTANTS ====================

const PRESET_OPTIONS_COLORS = {
  Sugar: "blue.500",
  "Sugar Quantity": "blue.500",
  "Hot/Cold": "blue.500",
  Milks: "blue.500",
};

const PRESET_OPTIONS = {
  Sugar: ["Raw", "White", "Sweetener"],
  "Hot/Cold": ["Hot", "Cold"],
  Milks: [
    "Full Cream Milk",
    "Skim Milk",
    "Lactose Free Milk",
    "Soy Milk",
    "Oat Milk",
    "Macadamia Milk",
    "Almond Milk",
    "Cashew Milk",
    "Coconut Milk",
    "Rice Milk",
  ],
};

const DEFAULT_SUGAR_SUB_OPTIONS = [
  { id: uuidv4(), name: "1 spoon", price: 0 },
  { id: uuidv4(), name: "1/2 spoon", price: 0 },
];

const SCROLL_DELAY = 50;
const MENU_TYPE_DRINKS = "Drinks";
const PRESET_TYPE_SUGAR = "Sugar";

// ==================== HELPER FUNCTIONS ====================

// Format currency in Australian Dollars (AUD)
const formatAUD = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "$0.00";
  }
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Parse price input (remove currency symbols and format)
const parsePriceInput = (text) => {
  if (!text) return "";
  // Remove currency symbols, spaces, and non-numeric characters except decimal point
  let cleaned = text.replace(/[^\d.]/g, "");
  // Ensure only one decimal point
  const parts = cleaned.split(".");
  if (parts.length > 2) {
    cleaned = parts[0] + "." + parts.slice(1).join("");
  }
  // Limit to 2 decimal places
  if (parts.length === 2 && parts[1].length > 2) {
    cleaned = parts[0] + "." + parts[1].substring(0, 2);
  }
  return cleaned;
};

const createNewDishOption = () => ({
  title: "",
  required: false,
  id: uuidv4(),
  optionItems: [],
});

const createNewOptionItem = () => ({
  name: "",
  price: 0,
  id: uuidv4(),
});

const validateOption = (option) => {
  if (!option.title || option.title.length === 0) {
    return { valid: false, message: "Please fill all Dish Option information" };
  }

  const hasInvalidItems = option.optionItems.some(
    (item) => !item.name || item.name.length === 0 || isNaN(item.price)
  );

  if (hasInvalidItems) {
    return {
      valid: false,
      message: "Please fill all Dish Option information correctly",
    };
  }

  return { valid: true };
};

// ==================== SUB-COMPONENTS ====================

const DishOptionTitle = ({ edit, option, onTitleChange, onRequiredChange }) => {
  return (
    <HStack justifyContent="space-between" alignItems="center" marginY={3}>
      {edit ? (
        <Input
          placeholder="Option Title"
          value={option.title}
          onChangeText={onTitleChange}
          flex={1}
          marginRight={3}
        />
      ) : (
        <Text fontSize="md" bold flex={1}>
          {option.title}
        </Text>
      )}
      <HStack alignItems="center" space={2}>
        <Checkbox
          onChange={edit ? onRequiredChange : undefined}
          isChecked={option.required}
        />
        <Text fontSize="sm">Required</Text>
      </HStack>
    </HStack>
  );
};

const DishOptionItem = ({
  item,
  onOptionChange,
  edit,
  onDeleteItem,
  innerRef,
}) => {
  const handleNameChange = useCallback(
    (text) => {
      onOptionChange(text, undefined, item);
    },
    [onOptionChange, item]
  );

  const handlePriceChange = useCallback(
    (text) => {
      const parsedPrice = parsePriceInput(text);
      // Convert to number, default to 0 if empty or invalid
      const numericPrice = parsedPrice === "" ? 0 : parseFloat(parsedPrice) || 0;
      onOptionChange(undefined, numericPrice, item);
    },
    [onOptionChange, item]
  );

  const handleDelete = useCallback(() => {
    onDeleteItem(item);
  }, [onDeleteItem, item]);

  return (
    <HStack
      ref={innerRef}
      justifyContent="space-between"
      alignItems="center"
      marginY={2}
      marginX={1}
      paddingX={2}
      paddingY={2}
      backgroundColor={!edit ? "gray.100" : "transparent"}
      borderRadius={4}
      space={3}
    >
      {edit ? (
        <>
          <Input
            placeholder="Item name"
            value={item.name}
            onChangeText={handleNameChange}
            flex={2}
            size="sm"
          />
          <HStack
            flex={1}
            alignItems="center"
            borderWidth={1}
            borderColor="gray.300"
            borderRadius={4}
            backgroundColor="white"
            paddingLeft={3}
            paddingRight={2}
            minHeight={8}
          >
            <Text fontSize="sm" color="gray.600" fontWeight="medium" marginRight={1}>
              $
            </Text>
            <Input
              placeholder="0.00"
              value={item.price !== undefined && item.price !== null ? item.price.toString() : ""}
              onChangeText={handlePriceChange}
              flex={1}
              size="sm"
              keyboardType="decimal-pad"
              borderWidth={0}
              paddingLeft={0}
              paddingRight={0}
              backgroundColor="transparent"
            />
          </HStack>
          <IconButton
            onPress={handleDelete}
            size="sm"
            colorScheme="red"
            variant="ghost"
          >
            <DeleteIcon />
          </IconButton>
        </>
      ) : (
        <>
          <Text flex={2} fontSize="sm">
            {item.name}
          </Text>
          <Text flex={1} fontSize="sm" bold>
            {formatAUD(item.price)}
          </Text>
        </>
      )}
    </HStack>
  );
};

const PresetButtons = ({
  menuType,
  isEditing,
  currentPreset,
  onPresetToggle,
}) => {
  if (menuType !== MENU_TYPE_DRINKS || !isEditing) {
    return null;
  }

  return (
    <VStack space={3} marginY={3} paddingY={2}>
      <Text fontSize="sm" color="gray.600" fontWeight="medium" marginBottom={1}>
        Presets:
      </Text>
      <HStack space={2} flexWrap="wrap">
        {Object.keys(PRESET_OPTIONS).map((category) => (
          <Button
            key={category}
            size="sm"
            bg="transparent"
            borderWidth={1.5}
            borderColor={
              currentPreset === category
                ? PRESET_OPTIONS_COLORS[category]
                : "gray.300"
            }
            borderRadius="md"
            paddingX={4}
            paddingY={2}
            _hover={{
              bg: "gray.50",
              borderColor: PRESET_OPTIONS_COLORS[category] || "black",
            }}
            _pressed={{
              bg: "gray.100",
              borderColor: PRESET_OPTIONS_COLORS[category] || "black",
            }}
            _text={{
              color: PRESET_OPTIONS_COLORS[category] || "black",
              fontSize: "sm",
              fontWeight: currentPreset === category ? "semibold" : "normal",
            }}
            onPress={() => onPresetToggle(category)}
          >
            {category}
          </Button>
        ))}
      </HStack>
    </VStack>
  );
};

const PresetOptionButtons = ({
  presetType,
  onOptionSelect,
  selectedPreset,
}) => {
  if (!presetType) return null;

  const options = PRESET_OPTIONS[presetType] || [];
  const color = PRESET_OPTIONS_COLORS[presetType] || "black";

  return (
    <VStack space={2} marginY={3} paddingY={2}>
      <Text fontSize="sm" color="gray.600" fontWeight="medium" marginBottom={1}>
        {presetType} Options:
      </Text>
      <HStack space={2} flexWrap="wrap">
        {options.map((opt) => (
          <Button
            key={opt}
            size="sm"
            bg={color}
            borderRadius="md"
            paddingX={4}
            paddingY={2}
            _hover={{ bg: color, opacity: 0.9 }}
            _pressed={{ bg: color, opacity: 0.8 }}
            _text={{ color: "white", fontSize: "sm", fontWeight: "medium" }}
            onPress={() => onOptionSelect(opt)}
          >
            {opt}
          </Button>
        ))}
      </HStack>
    </VStack>
  );
};

const SubOptionItemsList = ({
  subOptionItems,
  isEditing,
  onItemChange,
  onItemDelete,
  lastItemRef,
  presetType,
}) => {
  if (!subOptionItems || Object.keys(subOptionItems).length === 0) {
    return null;
  }

  return (
    <VStack space={4} marginY={3}>
      {Object.entries(subOptionItems).map(([key, items]) => (
        <Box key={key} marginY={3} paddingX={2}>
          {items.length > 0 && (
            <Text
              marginBottom={3}
              fontSize="sm"
              fontWeight="semibold"
              color="gray.700"
            >
              Pricing for {key} {presetType || ""}
            </Text>
          )}
          <VStack space={2}>
            {items.map((item, index) => {
              const isLast = index === items.length - 1;
              return (
                <DishOptionItem
                  key={item.id}
                  innerRef={isLast ? lastItemRef : null}
                  edit={isEditing}
                  item={item}
                  onOptionChange={(name, price, item) =>
                    onItemChange(name, price, item, key)
                  }
                  onDeleteItem={(item) => onItemDelete(item, key)}
                />
              );
            })}
          </VStack>
        </Box>
      ))}
    </VStack>
  );
};

const OptionItemList = ({
  optionItems,
  isEditing,
  onItemChange,
  onItemDelete,
  lastItemRef,
}) => {
  return (
    <VStack space={2} marginY={2}>
      {optionItems.map((item, index) => {
        const isLast = index === optionItems.length - 1;
        return (
          <DishOptionItem
            key={item.id}
            innerRef={isLast ? lastItemRef : null}
            edit={isEditing}
            item={item}
            onOptionChange={onItemChange}
            onDeleteItem={onItemDelete}
          />
        );
      })}
    </VStack>
  );
};

// ==================== MAIN COMPONENT ====================

const DishOptions = ({
  dish,
  menuType,
  closeHandler,
  onDishOptionEdited,
  onDishOptionDeleted,
}) => {
  // State
  const [dishOptions, setDishOptions] = useState([]);
  const [currentOptionId, setCurrentOptionId] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState("");
  const [addedOptionId, setAddedOptionId] = useState(null);
  const [showCloseAlert, setShowCloseAlert] = useState(false);
  const [incompletePresetsList, setIncompletePresetsList] = useState([]);
  const [hasUnsaved, setHasUnsaved] = useState(false);

  // Refs
  const scrollViewRef = useRef(null);
  const lastOptionRef = useRef(null);
  const lastSubOptionRef = useRef(null);
  const cancelRef = useRef(null);
  const firestore = getFirestore();

  // Reset state when modal closes
  useEffect(() => {
    if (!dish) {
      setCurrentOptionId(null);
      setAddedOptionId(null);
      setSelectedPreset("");
      // Reset dishOptions - will be reloaded when modal reopens
      setDishOptions([]);
    }
  }, [dish]);

  // Initialize dish options from prop
  useEffect(() => {
    if (!dish) return;
    const initialOptions = dish?.dishOptions ? [...dish.dishOptions] : [];
    setDishOptions(initialOptions);
  }, [dish]);

  // Subscribe to Firestore updates
  useEffect(() => {
    if (!dish?.id) return;

    const unsubscribe = firestore
      .collection("DishDetails")
      .doc(dish.id)
      .collection("dishOptions")
      .onSnapshot((snapshot) => {
        const options = snapshot.docs.map((doc) => doc.data());
        setDishOptions(options);
        setCurrentOptionId(null);
        setAddedOptionId(null);
      });

    return () => unsubscribe();
  }, [dish, firestore]);

  // Create new sugar sub-options with unique IDs
  const createSugarSubOptions = useCallback(() => {
    return DEFAULT_SUGAR_SUB_OPTIONS.map((item) => ({
      ...item,
      id: uuidv4(),
    }));
  }, []);

  // Find current option
  const currentOption = useMemo(() => {
    return dishOptions.find((opt) => opt.id === currentOptionId);
  }, [dishOptions, currentOptionId]);

  // Update option helper
  const updateOption = useCallback(
    (updater) => {
      setDishOptions((options) => {
        const updated = [...options];
        const index = updated.findIndex((o) => o.id === currentOptionId);
        if (index !== -1) {
          updater(updated[index]);
        }
        return updated;
      });
    },
    [currentOptionId]
  );

  // Add new dish option
  const handleAddDishOption = useCallback(() => {
    setSelectedPreset("");
    const newOption = createNewDishOption();
    setDishOptions((options) => [...options, newOption]);
    setCurrentOptionId(newOption.id);
    setAddedOptionId(newOption.id);

    setTimeout(() => {
      lastOptionRef.current?.scrollIntoView({ behavior: "smooth" });
    }, SCROLL_DELAY);
  }, []);

  // Save edited option
  const handleSaveOption = useCallback(() => {
    if (!currentOption) return;

    const validation = validateOption(currentOption);
    if (!validation.valid) {
      Notiflix.Report.Warning("Warning", validation.message, "Close");
      return;
    }

    onDishOptionEdited(dish, currentOption);
    setCurrentOptionId(null);
  }, [currentOption, dish, onDishOptionEdited]);

  // Delete option
  const handleDeleteOption = useCallback(
    (optionId) => {
      const option = dishOptions.find((o) => o.id === optionId);
      if (!option) {
        Notiflix.Report.Warning(
          "Warning",
          "Please fill all Dish Option information correctly",
          "Close"
        );
        return;
      }

      if (option.id === addedOptionId) {
        setDishOptions((options) =>
          options.filter((o) => o.id !== addedOptionId)
        );
      } else {
        onDishOptionDeleted(dish, option);
      }
    },
    [dishOptions, addedOptionId, dish, onDishOptionDeleted]
  );

  // Edit option properties
  const handleEditOption = useCallback(
    (updates) => {
      updateOption((option) => {
        Object.assign(option, updates);
      });
    },
    [updateOption]
  );

  // Add option item
  const handleAddOptionItem = useCallback(() => {
    updateOption((option) => {
      if (option.title === PRESET_TYPE_SUGAR && selectedPreset) {
        if (!option.subOptionItems) {
          option.subOptionItems = {};
        }
        if (!option.subOptionItems[selectedPreset]) {
          option.subOptionItems[selectedPreset] = createSugarSubOptions();
        } else {
          option.subOptionItems[selectedPreset] = [
            ...option.subOptionItems[selectedPreset],
            createNewOptionItem(),
          ];
        }
      } else {
        option.optionItems = [...option.optionItems, createNewOptionItem()];
      }
    });
  }, [updateOption, selectedPreset, createSugarSubOptions]);

  // Update option item
  const handleUpdateOptionItem = useCallback(
    (name, price, item) => {
      updateOption((option) => {
        const itemIndex = option.optionItems.findIndex(
          (i) => i.id === item.id
        );
        if (itemIndex !== -1) {
          if (name !== undefined) option.optionItems[itemIndex].name = name;
          if (price !== undefined) {
            const numericPrice = typeof price === "string" ? parseFloat(price) || 0 : price;
            option.optionItems[itemIndex].price = numericPrice;
          }
        }
      });
    },
    [updateOption]
  );

  // Update sub-option item
  const handleUpdateSubOptionItem = useCallback(
    (name, price, item, sugarType) => {
      if (!sugarType) return;

      updateOption((option) => {
        if (!option.subOptionItems?.[sugarType]) return;

        // Create a new array to avoid mutation issues
        const updatedItems = [...option.subOptionItems[sugarType]];
        const itemIndex = updatedItems.findIndex((i) => i.id === item.id);
        
        if (itemIndex !== -1) {
          // Create a new object for the updated item
          updatedItems[itemIndex] = {
            ...updatedItems[itemIndex],
            ...(name !== undefined && { name }),
            ...(price !== undefined && {
              price: typeof price === "string" ? parseFloat(price) || 0 : price,
            }),
          };
          option.subOptionItems[sugarType] = updatedItems;
        }
      });
    },
    [updateOption]
  );

  // Delete option item
  const handleDeleteOptionItem = useCallback(
    (item) => {
      updateOption((option) => {
        option.optionItems = option.optionItems.filter(
          (i) => i.id !== item.id
        );
      });
    },
    [updateOption]
  );

  // Delete sub-option item
  const handleDeleteSubOptionItem = useCallback(
    (item, sugarType) => {
      if (!sugarType) return;

      updateOption((option) => {
        if (option.subOptionItems?.[sugarType]) {
          option.subOptionItems[sugarType] = option.subOptionItems[
            sugarType
          ].filter((i) => i.id !== item.id);
        }
      });
    },
    [updateOption]
  );

  // Toggle preset
  const handlePresetToggle = useCallback(
    (category) => {
      updateOption((option) => {
        option.title = category;
        option.optionItems = [];
        option.required = false; // Reset required checkbox when switching presets
        option.showPreset =
          option.showPreset === category ? null : category;
      });
    },
    [updateOption]
  );

  // Select preset option
  const handlePresetOptionSelect = useCallback(
    (optionName) => {
      setSelectedPreset(optionName);
      updateOption((option) => {
        // Add to optionItems if not already present
        if (
          option.optionItems.length === 0 ||
          !option.optionItems.some((item) => item?.name === optionName)
        ) {
          option.optionItems = [
            ...option.optionItems,
            { id: uuidv4(), name: optionName, price: 0 },
          ];
        }

        // Handle sub-options for Sugar
        if (option.showPreset === PRESET_TYPE_SUGAR) {
          if (!option.subOptionItems) {
            option.subOptionItems = {};
          }
          if (
            !option.subOptionItems.hasOwnProperty(optionName) ||
            option.subOptionItems[optionName]?.length === 0
          ) {
            option.subOptionItems[optionName] = createSugarSubOptions();
          }
        } else {
          option.subOptionItems = null;
        }
      });

      setTimeout(() => {
        lastSubOptionRef.current?.scrollIntoView({ behavior: "smooth" });
      }, SCROLL_DELAY);
    },
    [updateOption, createSugarSubOptions]
  );

  // Check for incomplete options (preset selected but no items chosen) or unsaved changes
  const hasIncompleteOptions = useCallback(() => {
    return dishOptions.some((option) => {
      const isEditing = option.id === currentOptionId;
      
      // Check if option has a preset selected
      if (option.showPreset) {
        // For Sugar preset, check if any sub-options have items
        if (option.showPreset === PRESET_TYPE_SUGAR) {
          if (!option.subOptionItems || Object.keys(option.subOptionItems).length === 0) {
            return true; // Preset selected but no sugar type chosen
          }
          // Check if editing and has unsaved changes
          if (isEditing && option.subOptionItems && Object.keys(option.subOptionItems).length > 0) {
            return true; // Has items selected but not saved
          }
        } else {
          // For other presets (Milks, Hot/Cold), check if optionItems is empty
          if (!option.optionItems || option.optionItems.length === 0) {
            return true; // Preset selected but no option chosen
          }
          // Check if editing and has unsaved changes
          if (isEditing && option.optionItems && option.optionItems.length > 0) {
            return true; // Has items selected but not saved
          }
        }
      }
      return false;
    });
  }, [dishOptions, currentOptionId]);

  // Get incomplete option names for the warning message
  const getIncompleteOptionNames = useCallback(() => {
    const incompleteOptions = dishOptions.filter((option) => {
      const isEditing = option.id === currentOptionId;
      
      if (option.showPreset) {
        if (option.showPreset === PRESET_TYPE_SUGAR) {
          // Check if no sugar type chosen OR has items but not saved
          if (!option.subOptionItems || Object.keys(option.subOptionItems).length === 0) {
            return true;
          }
          if (isEditing && option.subOptionItems && Object.keys(option.subOptionItems).length > 0) {
            return true;
          }
        } else {
          // Check if no option chosen OR has items but not saved
          if (!option.optionItems || option.optionItems.length === 0) {
            return true;
          }
          if (isEditing && option.optionItems && option.optionItems.length > 0) {
            return true;
          }
        }
      }
      return false;
    });
    
    return incompleteOptions
      .map((option) => {
        const isEditing = option.id === currentOptionId;
        if (isEditing && option.showPreset) {
          return `${option.showPreset} (unsaved)`;
        }
        return option.showPreset;
      })
      .filter((name, index, self) => self.indexOf(name) === index); // Remove duplicates
  }, [dishOptions, currentOptionId]);

  // Check if there are unsaved changes (options with items but not saved)
  const hasUnsavedChanges = useCallback(() => {
    return dishOptions.some((option) => {
      const isEditing = option.id === currentOptionId;
      if (!isEditing || !option.showPreset) return false;
      
      if (option.showPreset === PRESET_TYPE_SUGAR) {
        return option.subOptionItems && Object.keys(option.subOptionItems).length > 0;
      } else {
        return option.optionItems && option.optionItems.length > 0;
      }
    });
  }, [dishOptions, currentOptionId]);

  // Handle modal close with validation
  const handleClose = useCallback(() => {
    if (hasIncompleteOptions()) {
      const incompletePresets = getIncompleteOptionNames();
      const unsaved = hasUnsavedChanges();
      setIncompletePresetsList(incompletePresets);
      setHasUnsaved(unsaved);
      setShowCloseAlert(true);
    } else {
      closeHandler();
    }
  }, [hasIncompleteOptions, getIncompleteOptionNames, hasUnsavedChanges, closeHandler]);

  // Handle confirmed close
  const handleConfirmClose = useCallback(() => {
    setShowCloseAlert(false);
    setHasUnsaved(false);
    closeHandler();
  }, [closeHandler]);

  // Handle cancel close
  const handleCancelClose = useCallback(() => {
    setShowCloseAlert(false);
    setHasUnsaved(false);
  }, []);

  return (
    <>
      <Modal isOpen={!!dish} onClose={handleClose} size="xl">
        <Modal.Content maxWidth="90%" maxHeight="90%">
          <Modal.CloseButton />
          <Modal.Header paddingX={5} paddingY={4} paddingRight={10} borderBottomWidth={1}>
            <HStack alignItems="center" justifyContent="space-between" width="100%" paddingRight={2}>
              <Text fontSize="xl" fontWeight="bold" flex={1}>
                Dish Options
              </Text>
              <Button
                size="sm"
                colorScheme="blue"
                onPress={handleAddDishOption}
                disabled={!!addedOptionId}
                borderRadius="md"
                marginRight={8}
              >
                Add +
              </Button>
            </HStack>
          </Modal.Header>
          <Modal.Body paddingX={5} paddingY={4}>
            <ScrollView
              ref={scrollViewRef}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={true}
            >
              {dishOptions.length === 0 ? (
                <Center minHeight="200px" justifyContent="center">
                  <VStack space={3} alignItems="center">
                    <Text fontSize="lg" color="gray.500" fontWeight="medium">
                      No options available
                    </Text>
                    <Text fontSize="sm" color="gray.400" textAlign="center">
                      Click "Add +" to create your first dish option
                    </Text>
                  </VStack>
                </Center>
              ) : (
                <>
                  <Box
                    backgroundColor="yellow.50"
                    padding={3}
                    borderRadius="md"
                    marginBottom={4}
                    borderLeftWidth={3}
                    borderLeftColor="yellow.400"
                  >
                    <Text fontSize="sm" color="yellow.800" fontWeight="medium">
                      Please select one of the presets below or enter the title of
                      the option.
                    </Text>
                  </Box>

                  <VStack space={4}>
                    {dishOptions.map((option, index) => {
                  const isLast = index === dishOptions.length - 1;
                  const isEditing = option.id === currentOptionId;

                  return (
                    <Box
                      key={option.id}
                      ref={isLast ? lastOptionRef : null}
                      borderWidth={1.5}
                      borderColor={isEditing ? "blue.300" : "gray.200"}
                      borderRadius="lg"
                      padding={4}
                      backgroundColor={isEditing ? "blue.50" : "white"}
                      shadow={isEditing ? 2 : 0}
                    >
                      {/* Action Buttons */}
                      <HStack justifyContent="flex-end" marginBottom={2} space={2}>
                        {isEditing ? (
                          <IconButton
                            onPress={handleSaveOption}
                            size="sm"
                            colorScheme="green"
                            borderRadius="md"
                          >
                            <CheckIcon />
                          </IconButton>
                        ) : (
                          <IconButton
                            onPress={() => setCurrentOptionId(option.id)}
                            size="sm"
                            colorScheme="blue"
                            borderRadius="md"
                          >
                            <MdModeEditOutline />
                          </IconButton>
                        )}
                        <IconButton
                          onPress={() => handleDeleteOption(option.id)}
                          size="sm"
                          colorScheme="red"
                          borderRadius="md"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </HStack>

                    {/* Preset Buttons */}
                    <PresetButtons
                      menuType={menuType}
                      isEditing={isEditing}
                      currentPreset={option.showPreset}
                      onPresetToggle={handlePresetToggle}
                    />

                    {/* Preset Option Buttons */}
                    {isEditing && option.showPreset && (
                      <PresetOptionButtons
                        presetType={option.showPreset}
                        onOptionSelect={handlePresetOptionSelect}
                        selectedPreset={selectedPreset}
                      />
                    )}

                    {/* Option Title */}
                    <DishOptionTitle
                      edit={isEditing}
                      option={option}
                      onTitleChange={(title) =>
                        handleEditOption({ title })
                      }
                      onRequiredChange={(required) =>
                        handleEditOption({ required })
                      }
                    />

                    {/* Sub-Option Items (for Sugar) */}
                    {option.showPreset === PRESET_TYPE_SUGAR &&
                      option.subOptionItems && (
                        <SubOptionItemsList
                          subOptionItems={option.subOptionItems}
                          isEditing={isEditing}
                          onItemChange={handleUpdateSubOptionItem}
                          onItemDelete={handleDeleteSubOptionItem}
                          lastItemRef={lastSubOptionRef}
                          presetType={option.showPreset}
                        />
                      )}

                    {/* Regular Option Items */}
                    {option.showPreset !== PRESET_TYPE_SUGAR && (
                      <OptionItemList
                        optionItems={option.optionItems}
                        isEditing={isEditing}
                        onItemChange={handleUpdateOptionItem}
                        onItemDelete={handleDeleteOptionItem}
                        lastItemRef={lastSubOptionRef}
                      />
                    )}

                      {/* Add Item Button */}
                      {isEditing && (
                        <Button
                          size="sm"
                          marginTop={4}
                          colorScheme="blue"
                          variant="outline"
                          borderRadius="md"
                          onPress={handleAddOptionItem}
                          alignSelf="flex-start"
                        >
                          Add Option Item
                        </Button>
                      )}
                    </Box>
                  );
                })}
                  </VStack>
                </>
              )}
            </ScrollView>
          </Modal.Body>
        </Modal.Content>
      </Modal>
      
      <AlertDialog leastDestructiveRef={cancelRef} isOpen={showCloseAlert} onClose={handleCancelClose}>
        <AlertDialog.Content>
          <AlertDialog.CloseButton />
          <AlertDialog.Header>Unsaved Changes</AlertDialog.Header>
          <AlertDialog.Body>
            <VStack space={3}>
              {hasUnsaved ? (
                <>
                  <Text>
                    You have made changes to preset option(s): <Text bold>{incompletePresetsList.join(", ")}</Text>, but haven't saved them yet.
                  </Text>
                  <Text fontWeight="medium" color="red.600">
                    Please click the checkmark (✓) button to save this option before closing.
                  </Text>
                  <Text>
                    If you close now, your changes will not be saved.
                  </Text>
                </>
              ) : (
                <>
                  <Text>
                    You have selected preset option(s): <Text bold>{incompletePresetsList.join(", ")}</Text>, but haven't chosen any specific options yet.
                  </Text>
                  <Text>
                    If you close now, this option will not be saved.
                  </Text>
                </>
              )}
              <Text fontWeight="medium" color="red.600">
                Do you want to continue and discard these changes?
              </Text>
            </VStack>
          </AlertDialog.Body>
          <AlertDialog.Footer>
            <Button.Group space={2}>
              <Button variant="unstyled" colorScheme="coolGray" onPress={handleCancelClose} ref={cancelRef}>
                Cancel
              </Button>
              <Button colorScheme="danger" onPress={handleConfirmClose}>
                Yes, Close
              </Button>
            </Button.Group>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
    </>
  );
};

export default DishOptions;
